import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Button,
  Popover,
  List,
  Typography,
  Divider
} from '@mui/material';
import GridOnIcon from '@mui/icons-material/GridOn';
import HearingIcon from '@mui/icons-material/Hearing';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ColorLayerManager from './ColorLayerManager';
import NeckViewPanel from './NeckViewPanel';
import * as Tone from 'tone';
import { createMixBus } from '../utils/playbackMix';
import { loadSampleBank, sampleUrls } from '../utils/sampleBanks';
import { connectReverbSend, whenReverbReady } from '../utils/reverbBus';
import { ALL_NOTES, resolveAllNotesIndex } from '../utils/musicTheory';
import { isDesktopBrowser } from '../utils/platformUtils';

// Color palette configuration
const COLOR_PALETTE = [
  { id: 'orange', hex: '#FF9800', name: 'Orange', suffix: '' },
  { id: 'red', hex: '#F44336', name: 'Red', suffix: 'red' },
  { id: 'green', hex: 'var(--hl-root)', name: 'Green', suffix: 'green' },
  { id: 'blue', hex: '#60a5fa', name: 'Blue', suffix: 'blue' }
];

// Custom circular note icon component to match fretboard styling
const NoteCircleIcon = ({ color, size = '1.2rem' }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: '#000000',
      border: `2.5px solid ${color}`,
    }}
  />
);

const NeckLayers = ({
  highlightArpeggios,
  setHighlightArpeggios,
  showConnectionLines,
  setShowConnectionLines,
  showWholeScale,
  setShowWholeScale,
  lessonMode = false,
  selectedNotes = [],
  onClearSelectedNotes,
  onStartNoteSelectionMode,
  isStartNoteSelectionMode = false,
  currentColor = 'orange',
  onColorSelect,
  visibleColors = ['orange', 'red', 'green', 'blue'],
  onToggleColorVisibility,
  activeScale,
  selectedArpeggio,
  setSelectedArpeggio,
  isFretboardLocked = false,
  onToggleFretboardLock,
  onShowPatternSnackbar,
  isPaintMode = false,
  onPaintModeChange,
  activeChordNote,
  playingNote,
  highlightChordNotes,
  setHighlightChordNotes,
  noteDisplayMode = 'none',
  setNoteDisplayMode,
  className = '',
  isCompactMode = false,
  rootNote,
  currentMode,
  trackedDegree = 1,
  setTrackedDegree,
  chordExtension = '3',
  earSequence = [],
  onEarStep
}) => {
  const [patternAnchorEl, setPatternAnchorEl] = useState(null);
  const [viewAnchorEl, setViewAnchorEl] = useState(null);
  // Lu une seule fois : la mise en page bureau ne change pas en cours de session
  const [desktopBrowser] = useState(isDesktopBrowser);
  const [hearPortalTarget, setHearPortalTarget] = useState(null);
  const longPressTimerRef = useRef(null);
  const [isPlayingScale, setIsPlayingScale] = useState(false);
  const scalePlaybackRef = useRef(null);
  const synthRef = useRef(null);
  const droneSynthRef = useRef(null);
  const mixNodesRef = useRef([]);

  const playScaleNotes = useCallback(async () => {
    if (isPlayingScale) {
      // Stop playback
      if (scalePlaybackRef.current) {
        clearTimeout(scalePlaybackRef.current);
        scalePlaybackRef.current = null;
      }
      if (synthRef.current) {
        synthRef.current.triggerRelease();
      }
      if (droneSynthRef.current) {
        droneSynthRef.current.triggerRelease();
      }
      setIsPlayingScale(false);
      if (onEarStep) onEarStep(null);
      return;
    }

    if (!rootNote || !currentMode) return;

    await Tone.start();

    if (!synthRef.current || !synthRef.current.loaded) {
      if (synthRef.current) {
        synthRef.current.dispose();
      }
      // Bus de mix puis envoi vers l'unique reverb (cf reverbBus) : la reverb
      // partagee n'est jamais jetee, seuls le bus et les deux gains de l'envoi
      // partent au demontage.
      const bus = createMixBus('piano');
      mixNodesRef.current.push(bus, connectReverbSend(bus, 0.55));
      await whenReverbReady();
      // Les echantillons viennent du registre partage : le piano du manche et la
      // banque d'accords jouent deja ces fichiers, ils ne sont plus telecharges
      // ni decodes une quatrieme fois ici. L'attente porte sur la banque et non
      // sur `Tone.loaded()`, qui est global.
      await loadSampleBank('piano').ready;
      synthRef.current = new Tone.Sampler({
        urls: sampleUrls('piano'),
        release: 1,
        volume: 6
      }).connect(bus);
    }

    // Start drone on root note
    if (!droneSynthRef.current) {
      const bus = createMixBus('drone');
      mixNodesRef.current.push(bus, connectReverbSend(bus, 0.6));
      await whenReverbReady();
      droneSynthRef.current = new Tone.Synth({
        oscillator: { type: 'sine' },
        envelope: { attack: 0.8, decay: 0, sustain: 1, release: 1.5 },
        volume: -36
      }).connect(bus);
    }
    // Drone on root
    const noteToMidi = (name, octave) => resolveAllNotesIndex(name) + (octave + 1) * 12;
    const midiToPlayable = (midi) => `${ALL_NOTES[midi % 12]}${Math.floor(midi / 12) - 1}`;
    const droneNote = midiToPlayable(noteToMidi(rootNote, 3));
    droneSynthRef.current.triggerAttack(droneNote);

    // Walk the displayed scale-shape sequence (3nps): each entry has a playable
    // note and its fret position, so the glow lights the exact notes shown.
    const sequence = earSequence;
    if (!sequence || sequence.length === 0) {
      droneSynthRef.current.triggerRelease();
      return;
    }

    setIsPlayingScale(true);

    const playNext = (index) => {
      if (index >= sequence.length) {
        if (droneSynthRef.current) {
          droneSynthRef.current.triggerRelease();
        }
        setIsPlayingScale(false);
        if (onEarStep) onEarStep(null);
        scalePlaybackRef.current = null;
        return;
      }
      synthRef.current.triggerAttackRelease(sequence[index].note, '8n');
      if (onEarStep) onEarStep(index);
      scalePlaybackRef.current = setTimeout(() => playNext(index + 1), 350);
    };

    playNext(0);
  }, [isPlayingScale, rootNote, currentMode, earSequence, onEarStep]);

  // La pastille du titre est montee plus haut dans l'arbre : sa cible n'existe
  // pas au premier rendu, d'ou la resolution apres montage.
  useEffect(() => {
    setHearPortalTarget(document.getElementById('hear-button-portal'));
  }, []);

  // Cleanup synth on unmount
  useEffect(() => {
    return () => {
      mixNodesRef.current.forEach(node => node.dispose());
      mixNodesRef.current = [];
      if (scalePlaybackRef.current) clearTimeout(scalePlaybackRef.current);
      if (synthRef.current) {
        synthRef.current.dispose();
        synthRef.current = null;
      }
      if (droneSynthRef.current) {
        droneSynthRef.current.dispose();
        droneSynthRef.current = null;
      }
    };
  }, []);
  const longPressTriggeredRef = useRef(false);

  const handleArpeggioClick = () => {
    // Toggle chord note highlighting
    if (setHighlightChordNotes) {
      setHighlightChordNotes(!highlightChordNotes);
    }
    setHighlightArpeggios(!highlightChordNotes);
  };

  const handleResetArpeggioSelection = () => {
    setSelectedArpeggio(null);
    setHighlightArpeggios(false);
    if (setHighlightChordNotes) {
      setHighlightChordNotes(false);
    }
  };

  // Pattern button handlers with long-press support
  const handlePatternPointerDown = (event) => {
    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      // Long press action - clear all patterns
      if (onClearSelectedNotes) {
        onClearSelectedNotes();
      }
      handleResetArpeggioSelection();
    }, 500);
  };

  const handlePatternPointerUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    // Only trigger click if long press wasn't triggered
    if (!longPressTriggeredRef.current) {
      if (onPaintModeChange) {
        onPaintModeChange(!isPaintMode);
      }
    }
  };

  const handlePatternPointerCancel = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handlePatternClose = () => {
    setPatternAnchorEl(null);
  };

  const handleStartNoteSelection = () => {
    setPatternAnchorEl(null);
    if (onShowPatternSnackbar) {
      onShowPatternSnackbar(true);
    }
    if (onStartNoteSelectionMode) {
      onStartNoteSelectionMode(true);
    }
  };

  useEffect(() => {
    if (!highlightArpeggios && selectedArpeggio) {
      handleResetArpeggioSelection();
    }
    if (!highlightArpeggios && highlightChordNotes && setHighlightChordNotes) {
      setHighlightChordNotes(false);
    }
  }, [highlightArpeggios, selectedArpeggio, highlightChordNotes, setHighlightChordNotes]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const patternOpen = Boolean(patternAnchorEl);
  const patternId = patternOpen ? 'pattern-popover' : undefined;

  // Shared button styles
  // Rangee secondaire : un cran sous la pastille du titre (37px), qui reste
  // l'element dominant de l'entete.
  const buttonBaseStyles = {
    minWidth: 52,
    minHeight: 34,
    flexDirection: 'column',
    gap: 0,
    px: 1,
    py: 0.25,
    // Meme amplitude d'arrondi que la pastille du titre (HeaderPills, 24px)
    borderRadius: '24px',
    textTransform: 'none',
    fontSize: '0.62rem',
    // Sans ca, le line-height MUI par defaut (1.75) gonfle la legende et le
    // bouton depasse la pastille du titre.
    lineHeight: 1.1,
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.8)',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '&:active': {
      transform: 'scale(0.97)',
    },
  };

  const buttonActiveStyles = {
    ...buttonBaseStyles,
    color: 'var(--accent)',
  };

  // Vue courante : showWholeScale = true met en avant les notes de l'accord
  // (celles qu'on conseille de jouer), false ouvre tout le pattern de gamme.
  // Nomme par le resultat sonore, pas par la theorie. Le segment allume est
  // l'etat, pas l'action.
  const VIEW_SEGMENTS = [
    { id: 'safe', label: 'Safe', wholeScale: true },
    { id: 'all', label: 'All', wholeScale: false },
  ];

  // Reglages d'affichage du manche, replies dans un popover : labels et degre
  // suivi par l'ancre verte. Un seul bouton dans la barre parce qu'en paysage
  // elle est portee dans la ligne du titre, bornee a 50vw avec overflow hidden,
  // ou des segmentes a plat seraient tronques en silence.
  const trackedLabel = { 1: 'Root', 3: '3rd', 5: '5th', 7: '7th' }[trackedDegree] || 'Root';
  const isTracking = trackedDegree !== 1;

  const viewButton = (
    <Button
      onClick={(e) => setViewAnchorEl(e.currentTarget)}
      aria-label="Neck display settings"
      aria-haspopup="dialog"
      aria-expanded={Boolean(viewAnchorEl)}
      sx={isTracking
        ? { ...buttonBaseStyles, color: 'var(--hl-root)' }
        : buttonBaseStyles}
    >
      <VisibilityIcon sx={{ fontSize: '1.15rem' }} />
      {/* La legende porte l'etat le plus vite perdu de vue : quand l'ancre a
          quitte la tonique, le manche ment si on ne le rappelle pas. */}
      <span>{isTracking ? trackedLabel : 'View'}</span>
    </Button>
  );

  const viewSelector = (
    <Box
      className="neck-view-selector"
      role="group"
      aria-label="Fretboard view"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        p: '3px',
        borderRadius: '24px',
        backgroundColor: 'rgba(255, 255, 255, 0.07)',
        // Pendant une lecon le manche n'affiche plus ni pattern ni notes fortes :
        // le segmente n'a plus de prise, il se grise sur All.
        opacity: lessonMode ? 0.45 : 1,
      }}
    >
      {VIEW_SEGMENTS.map((segment) => {
        const isSelected = lessonMode ? !segment.wholeScale : showWholeScale === segment.wholeScale;
        return (
          <Button
            key={segment.id}
            onClick={() => setShowWholeScale(segment.wholeScale)}
            aria-pressed={isSelected}
            disabled={lessonMode}
            disableRipple
            sx={{
              minWidth: 68,
              minHeight: 28,
              px: 1.5,
              borderRadius: '21px',
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: isSelected ? 700 : 500,
              lineHeight: 1,
              color: isSelected ? 'var(--accent)' : 'rgba(255, 255, 255, 0.6)',
              backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
              boxShadow: isSelected ? '0 1px 2px rgba(0, 0, 0, 0.35)' : 'none',
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.14)' : 'rgba(255, 255, 255, 0.05)',
              },
              // Le gris MUI par defaut est invisible sur le fond noir du manche.
              '&.Mui-disabled': {
                color: isSelected ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.4)',
              },
            }}
          >
            {segment.label}
          </Button>
        );
      })}
    </Box>
  );

  // Bouton Hear : action ponctuelle sur la gamme affichee dans la pastille du
  // titre, donc icone seule collee a cette pastille (repli dans la barre quand
  // la page n'a pas de pastille, comme VideoPage). Absent en lecon : le manche
  // n'y montre plus une gamme a ecouter mais les pas de la lecon, et la
  // pastille qu'il accompagne a elle aussi disparu.
  const hearButton = lessonMode ? null : (
    <Button
      onClick={playScaleNotes}
      aria-label={isPlayingScale ? 'Stop playing the scale' : 'Hear the scale'}
      sx={{
        minWidth: 37,
        width: 37,
        height: 37,
        p: 0,
        borderRadius: '50%',
        color: isPlayingScale ? 'var(--accent)' : 'rgba(255, 255, 255, 0.9)',
        backgroundColor: isPlayingScale ? 'rgba(var(--accent-rgb), 0.18)' : '#111',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: isPlayingScale ? 'rgba(var(--accent-rgb), 0.26)' : '#1a1a1a',
        },
        '&:active': {
          transform: 'scale(0.95)',
        },
      }}
    >
      {isPlayingScale
        ? <StopRoundedIcon sx={{ fontSize: '1.4rem' }} />
        : <HearingIcon sx={{ fontSize: '1.3rem' }} />}
    </Button>
  );

  // Get portal target for landscape mode
  const portalTarget = isCompactMode
    ? document.getElementById('landscape-toolbar-portal')
    : null;

  const toolbarContent = (
    <Box
      className={`neck-layers-toolbar ${className}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        // En paysage la barre est injectee dans la ligne du titre : pas de marge.
        mt: portalTarget ? 0 : 1.75,
      }}
    >
      {/* Hear vit dans la pastille du titre ; ici seulement en repli */}
      {!hearPortalTarget && hearButton}

      {/* Vue du manche : gamme entiere ou notes de l'accord */}
      {viewSelector}

      {/* Reglages d'affichage du manche (labels + degre suivi par l'ancre) */}
      {viewButton}

      {/* Target Notes Button - temporarily disabled
      <Button
        onClick={handleArpeggioClick}
        sx={highlightChordNotes ? buttonActiveStyles : buttonBaseStyles}
      >
        <GridOnIcon sx={{ fontSize: '1.25rem' }} />
        <span>Target Notes</span>
      </Button>
      */}

      {/* Patterns Button (formerly Color Circle) - hidden for now, to be reintroduced as a feature update */}
      {false && (
      <Button
        onPointerDown={handlePatternPointerDown}
        onPointerUp={handlePatternPointerUp}
        onPointerCancel={handlePatternPointerCancel}
        onPointerLeave={handlePatternPointerCancel}
        sx={isPaintMode ? buttonActiveStyles : buttonBaseStyles}
      >
        <NoteCircleIcon
          color={COLOR_PALETTE.find(c => c.id === currentColor)?.hex || '#FF9800'}
          size="1.15rem"
        />
        <span>Patterns</span>
      </Button>
      )}

      {/* Reset Button - clears painted notes and target notes */}
      {(selectedNotes.length > 0 || highlightChordNotes) && (
        <Button
          onClick={() => {
            if (onClearSelectedNotes) {
              onClearSelectedNotes();
            }
            handleResetArpeggioSelection();
          }}
          sx={{
            ...buttonBaseStyles,
          }}
        >
          <RestartAltIcon sx={{ fontSize: '1.25rem' }} />
          <span>Reset</span>
        </Button>
      )}

      {/* Reglages d'affichage du manche. Ancre sous le bouton en portrait, mais
          la barre vit dans la ligne du titre en paysage : le popover s'ouvre
          alors vers le bas, du meme cote, pour ne pas sortir de l'ecran. */}
      <Popover
        open={Boolean(viewAnchorEl)}
        anchorEl={viewAnchorEl}
        onClose={() => setViewAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { bgcolor: '#15151A', borderRadius: '14px', border: desktopBrowser ? 0 : '1px solid rgba(255,255,255,0.08)' } }}
      >
        <NeckViewPanel
          noteDisplayMode={noteDisplayMode}
          onNoteDisplayModeChange={(next) => setNoteDisplayMode && setNoteDisplayMode(next)}
          trackedDegree={trackedDegree}
          onTrackedDegreeChange={(next) => setTrackedDegree && setTrackedDegree(next)}
          chordExtension={chordExtension}
        />
      </Popover>

      {/* Pattern Management Popover (hidden, can be re-enabled if needed) */}
      <Popover
        id={patternId}
        open={patternOpen}
        anchorEl={patternAnchorEl}
        onClose={handlePatternClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: { maxWidth: 400, bgcolor: '#1a1a1a' }
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle1" sx={{ p: 1, fontSize: '0.9rem', fontWeight: 500, color: '#fff' }}>
            Pattern
          </Typography>

          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              }
            }}
            onClick={handleStartNoteSelection}
          >
            <Box sx={{ padding: '8px', pointerEvents: 'none' }}>
              <NoteCircleIcon
                color={COLOR_PALETTE.find(c => c.id === currentColor)?.hex || '#FF9800'}
                size="1.4rem"
              />
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
              Select scale start note
            </Typography>
          </Box>

          <Divider sx={{ mx: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          <Typography variant="subtitle1" sx={{ p: 1, fontSize: '0.9rem', fontWeight: 500, color: '#fff' }}>
            Neck Layers
          </Typography>
          <List sx={{ width: '100%', maxWidth: 320, p: 0 }}>
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <ColorLayerManager
                currentColor={currentColor}
                onColorSelect={onColorSelect}
                visibleColors={visibleColors}
                onToggleColorVisibility={onToggleColorVisibility}
              />
            </Box>
          </List>
        </Box>
      </Popover>
    </Box>
  );

  const hearPortal = hearPortalTarget ? createPortal(hearButton, hearPortalTarget) : null;

  // In landscape mode, render toolbar into the header row via portal
  if (portalTarget) {
    return (
      <>
        {hearPortal}
        {createPortal(toolbarContent, portalTarget)}
      </>
    );
  }

  return (
    <>
      {hearPortal}
      {toolbarContent}
    </>
  );
};

export default NeckLayers;
